import { useEffect, useRef } from 'react';
import { useSession } from '@clerk/clerk-react';
import { toast } from 'react-toastify';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import { useMassMailContext } from '../context/MassMailContext';
import { AttachedFile } from '../types';

/**
 * Auto-fetches Odoo invoice PDFs that callers staged via
 * `state.pendingInvoiceAttachments` and appends them to `state.attachedFiles`.
 *
 * Used so flows like the incasso DossierDetailSheet can ship invoices as
 * email bijlagen without making the user wait on the prior screen.
 */
const base64ToFile = (base64: string, fileName: string): File => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  return new File([new Uint8Array(byteNumbers)], fileName, {
    type: 'application/pdf',
  });
};

interface InvoicePdfResult {
  invoiceName: string;
  pdf?: { data: string; name: string } | null;
  error?: string;
}

export const useInvoiceAttachmentPrefetch = () => {
  const { state, dispatch } = useMassMailContext();
  const { session } = useSession();
  const hasFetchedRef = useRef(false);

  // Keep a live ref to attachedFiles so the async fetch always reads the
  // latest list when it dispatches — otherwise files the user attached while
  // the PDF download was in flight would be wiped.
  const attachedFilesRef = useRef(state.attachedFiles);
  attachedFilesRef.current = state.attachedFiles;

  useEffect(() => {
    if (hasFetchedRef.current) return;
    if (!session) return;
    if (!state.pendingInvoiceAttachments || state.pendingInvoiceAttachments.length === 0) return;

    hasFetchedRef.current = true;
    const pending = state.pendingInvoiceAttachments;

    // Clear pending list immediately so a re-render can't kick off a duplicate
    // fetch even if the StrictMode double-effect lands before the network roundtrip
    dispatch({ type: 'SET_PENDING_INVOICE_ATTACHMENTS', payload: [] });

    const fetchInvoicePdfs = async () => {
      try {
        const response = await fetchWithAuth(
          `${API_URL}/rental/contracts/invoice-pdfs`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              invoices: pending.map((inv) => ({
                name: inv.name,
                companyId: inv.companyId ?? undefined,
              })),
            }),
          },
          session,
        );
        if (!response.ok) {
          throw new Error(`API returned ${response.status}`);
        }

        const results: InvoicePdfResult[] = await response.json();

        const newAttachments: AttachedFile[] = [];
        const failed: string[] = [];
        for (const result of results) {
          if (result.pdf?.data) {
            const fileName = result.pdf.name || `${result.invoiceName}.pdf`;
            const file = base64ToFile(result.pdf.data, fileName);
            newAttachments.push({
              id: `invoice-${result.invoiceName}-${Date.now()}`,
              file,
              name: fileName,
              size: file.size,
              type: file.type,
            });
          } else {
            failed.push(result.invoiceName);
          }
        }

        if (newAttachments.length > 0) {
          dispatch({
            type: 'SET_ATTACHED_FILES',
            payload: [...attachedFilesRef.current, ...newAttachments],
          });
          toast.success(
            `${newAttachments.length} factuur-PDF${
              newAttachments.length === 1 ? '' : "'s"
            } als bijlage toegevoegd`,
          );
        }
        if (failed.length > 0) {
          toast.warning(
            `Geen PDF beschikbaar voor: ${failed.join(', ')}`,
          );
        }
      } catch (error) {
        console.error('[useInvoiceAttachmentPrefetch] Error fetching invoice PDFs:', error);
        toast.error('Fout bij ophalen van factuur-PDFs als bijlage');
      }
    };

    fetchInvoicePdfs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, state.pendingInvoiceAttachments]);
};
